import React, {useState, useEffect, ComponentType} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {RouteComponentProps} from 'react-router-dom';
import {withRouter} from 'react-router-dom';
import {AssetGroup, Bundle} from '@deviceinsight/ng-ui-api-client';
import {fetchAllBundles} from '@deviceinsight/ng-ui-api-client';
import {ContentHeader, messageBoxSuccess} from '@deviceinsight/ng-ui-components';
import {Accordion, AccordionItem, SpinnerContainer, Button} from '@deviceinsight/ng-ui-basic-components';
import JobVariantForm from './create/JobVariantForm';
import JobDetailsForm from './create/JobDetailsForm';
import {ADMIN_CREATE_TEST_IDS, ADMIN_CREATE_JOB_VARIANT_CONFIGS} from './consts';
import type {CreateJobFormData, CreateJobRequest} from './types';
import {createJob} from '../../api/updateJobsApi';
import './AdminCreate.less';
import getAtLeastOneVariantDefined from './create/getAtLeastOneVariantDefined';
type OwnProps = {
	assetGroup: AssetGroup;
};
type Props = OwnProps & RouteComponentProps;
const HEADER_TITLE = {
	id: 'updatejobs.list.create',
	defaultValue: 'Create update job'
};
const INITIAL_FORM_STATE: CreateJobFormData = {
	...ADMIN_CREATE_JOB_VARIANT_CONFIGS,
	latestExecutionDate: undefined,
	description: undefined
} as unknown as CreateJobFormData;

const onSuccessfulCreate = (assetGroupId: number, push: (arg0: string) => void) => {
	messageBoxSuccess(<Trans i18nKey="updatejobs.create.createSuccess">The job was successfully created</Trans>);
	push(`/assetgroups/${assetGroupId}/updatejobs`);
};

const AdminCreate = ({assetGroup, history: {push}}: Props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [bundles, setBundles] = useState<Bundle[]>([]);
	const [formValues, setFormValues] = useState<CreateJobFormData>(INITIAL_FORM_STATE);

	const submit = () => {
		setLoading(true);
		const data = {
			description: formValues.description,
			latestExecutionDate: formValues.latestExecutionDate
		} as Record<string, any>;
		Object.keys(ADMIN_CREATE_JOB_VARIANT_CONFIGS).map((key) => {
			const stringIndexedFormValues = formValues as Record<string, any>;
			if (stringIndexedFormValues[key] && stringIndexedFormValues[key].version) {
				data[key] = {
					bundleId: stringIndexedFormValues[key].bundle.value,
					bundleVersionId: stringIndexedFormValues[key].version.value
				};
			}
		});

		createJob(assetGroup.id, data as CreateJobRequest)
			.then(() => {
				onSuccessfulCreate(assetGroup.id, push);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		setLoading(true);
		fetchAllBundles()
			.then(setBundles)
			.finally(() => setLoading(false));
	}, []);
	const atLeastOneVariantDefined = getAtLeastOneVariantDefined(formValues);
	return (
		<div className="admin-create">
			<ContentHeader title={HEADER_TITLE} parentPageLink={true} />
			<SpinnerContainer show={loading}>
				<div className="job-create-form column-wrapper" data-testid={ADMIN_CREATE_TEST_IDS.FORM}>
					<div className="job-create-variants column" data-testid={ADMIN_CREATE_TEST_IDS.VARIANTS}>
						{Object.keys(ADMIN_CREATE_JOB_VARIANT_CONFIGS).map((key) => {
							const config = ADMIN_CREATE_JOB_VARIANT_CONFIGS[key];
							return (
								<Accordion key={key}>
									{[
										<AccordionItem
											key={config.key}
											title={<Trans i18nKey={config.name.id}>{config.name.defaultValue}</Trans>}>
											<JobVariantForm
												bundles={bundles}
												formData={(formValues as Record<string, any>)[key]}
												setFormData={(data) => {
													setFormValues({...formValues, [key]: data});
												}}
											/>
										</AccordionItem>
									]}
								</Accordion>
							);
						})}
					</div>
					<div className="job-create-details column" data-testid={ADMIN_CREATE_TEST_IDS.DETAILS}>
						<JobDetailsForm
							heading={{
								i18nKey: 'updatejobs.create.jobDetails',
								defaultValue: 'Job Details'
							}}
							formData={formValues}
							setFormData={setFormValues}
						/>
						{atLeastOneVariantDefined && (
							<Button primary onClick={submit}>
								<Trans i18nKey="updatejobs.create.scheduleButton">Schedule firmware update</Trans>
							</Button>
						)}
					</div>
				</div>
			</SpinnerContainer>
		</div>
	);
};

export default withRouter(AdminCreate) as ComponentType<OwnProps>;
